import { useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import {
  RejectPendingChangeFormData,
  rejectPendingChangeSchema,
} from '@/features/public/bookingSummary/schemas/rejectPendingChangeSchema.ts';
import { REJECT_DIALOG } from './pendingChangeCopy.ts';

interface RejectPendingChangeDialogProps {
  isRejecting: boolean;
  onClose: () => void;
  onReject: (form: RejectPendingChangeFormData) => void;
}

/** Odrzucenie zmian przez zapisującego, z komentarzem, który organizator zobaczy przy paczce. */
const RejectPendingChangeDialog = ({
  isRejecting,
  onClose,
  onReject,
}: RejectPendingChangeDialogProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<RejectPendingChangeFormData>({
    resolver: debugZodResolver(rejectPendingChangeSchema),
    defaultValues: { comment: '' },
  });

  // Zamknięcie w trakcie wysyłki odmontowałoby okno, zanim klient zobaczy wynik.
  const close = () => {
    if (!isRejecting) onClose();
  };

  return (
    <Modal onClose={close} isDirty={isDirty} className="max-w-[520px]">
      {/* Nagłówek poza `<form>`: jego krzyżyk nie ma `type`, więc w formularzu wysłałby go. */}
      <ModalHeader
        title={REJECT_DIALOG.title}
        onClose={close}
        closeDisabled={isRejecting}
      />
      <form onSubmit={handleSubmit(onReject)}>
        <ModalContent>
          <p className="text-pretty text-sm text-neutral-600">
            {REJECT_DIALOG.description}
          </p>
          <TextareaInput
            label={REJECT_DIALOG.commentLabel}
            register={register('comment')}
            error={errors.comment?.message}
            hint={REJECT_DIALOG.commentHint}
            rows={4}
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton
            tone="ghost"
            size="sm"
            type="button"
            onClick={close}
            disabled={isRejecting}
          >
            {REJECT_DIALOG.cancelText}
          </ActionButton>
          <ActionButton
            tone="danger"
            size="sm"
            type="submit"
            isLoading={isRejecting}
          >
            {REJECT_DIALOG.confirmText}
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RejectPendingChangeDialog;
